import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { TransitionProps } from '@mui/material/transitions';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import FolderRounded from '@mui/icons-material/FolderRounded';
import { Tooltip, useTheme, IconButton } from '@mui/material';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { TreeItemLabel, TreeItemIconContainer } from '@mui/x-tree-view/TreeItem';
import { useTreeItem, UseTreeItemParameters } from '@mui/x-tree-view/useTreeItem';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view/TreeItemDragAndDropOverlay';

import useLocales from '../../../hooks/useLocales';
import { useSelector } from '../../../redux/store';
import Scrollbar from '../../../components/Scrollbar';
import folderService from '../../../services/folderService';
import { FolderTreeProp } from '../../../@types/folders/types';

// ----------------------------------------------------------------

type FolderTreeProps = {
  showDrawer: boolean;
  setShowFolderTree: (isOpen: boolean) => void;
  folderTreeExpandedItems: string[];
  setFolderTreeExpandedItems: (expandedItems: string[]) => void;
};

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

const StyledTreeItemRoot = styled('li')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  outline: 0,
  color: theme.palette.grey[400],
  ...theme.applyStyles('light', {
    color: theme.palette.grey[800],
  }),
}));

const StyledTreeItemContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  paddingLeft: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  borderRadius: theme.spacing(0.7),
  marginBottom: theme.spacing(0.25),
  marginTop: theme.spacing(0.25),
  fontWeight: 500,

  [`&[data-focused], &[data-selected]`]: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    ...theme.applyStyles('light', {
      backgroundColor: theme.palette.primary.main,
    }),
  },

  '&:not([data-focused], [data-selected]):hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: 'white',
    ...theme.applyStyles('light', {
      color: theme.palette.primary.main,
    }),
  },

  '&[data-disabled]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const StyledCollapse = styled(Collapse)({
  padding: 0,
});

function TransitionComponent(props: TransitionProps) {
  return <StyledCollapse {...props} />;
}

const StyledTreeItemLabelText = styled(Typography)({
  color: 'inherit',
  fontWeight: 500,
});

interface CustomLabelProps {
  children: React.ReactNode;
  icon?: React.ElementType;
  expandable?: boolean;
}

function CustomLabel({ icon: Icon, expandable, children, ...other }: CustomLabelProps) {
  return (
    <TreeItemLabel
      {...other}
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {Icon && (
        <Box
          component={Icon}
          className="labelIcon"
          sx={{ mr: 1, fontSize: '1.35rem', color: '#FFCA28 !important' }}
        />
      )}
      <StyledTreeItemLabelText variant="body2">{children}</StyledTreeItemLabelText>
    </TreeItemLabel>
  );
}

interface CustomTreeItemProps
  extends Omit<UseTreeItemParameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {}

const CustomTreeItem = React.forwardRef(
  (props: CustomTreeItemProps, ref: React.Ref<HTMLLIElement>) => {
    const { id, itemId, label, disabled, children, ...other } = props;

    const {
      getContextProviderProps,
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getLabelProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    // Open folder ikona rodo kuris folderis šiuo metu yra pasirinktas/atidarytas
    const icon = status.selected ? FolderOpenIcon : FolderRounded;

    return (
      <TreeItemProvider {...getContextProviderProps()}>
        <StyledTreeItemRoot {...getRootProps(other)}>
          <StyledTreeItemContent {...getContentProps()}>
            <TreeItemIconContainer {...getIconContainerProps()}>
              <TreeItemIcon status={status} />
            </TreeItemIconContainer>
            <CustomLabel
              {...getLabelProps({
                icon,
                expandable: status.expandable && status.expanded,
              })}
            />
            <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
          </StyledTreeItemContent>
          {children && <TransitionComponent {...getGroupTransitionProps()} />}
        </StyledTreeItemRoot>
      </TreeItemProvider>
    );
  }
);

const FolderTree: React.FC<FolderTreeProps> = ({
  showDrawer,
  setShowFolderTree,
  folderTreeExpandedItems,
  setFolderTreeExpandedItems,
}) => {
  const theme = useTheme();
  const { t } = useLocales();
  const { reloadDriveList } = useSelector((state) => state.dlxDrive);
  const [folderTree, setFolderTree] = useState<FolderTreeProp[]>([]);
  const navigate = useNavigate();
  const { folderId } = useParams();

  const isItemDisabled = (item: FolderTreeProp) => item.disabled;

  const getAllItemsWithChildrenItemIds = () => {
    const itemIds: string[] = [];
    const registerItemId = (item: FolderTreeProp) => {
      if (item.children?.length) {
        itemIds.push(item.id);
        item.children.forEach(registerItemId);
      }
    };
    folderTree.forEach(registerItemId);
    return itemIds;
  };

  const handleExpandClick = () => {
    if (folderTreeExpandedItems.length === 0) {
      setFolderTreeExpandedItems(getAllItemsWithChildrenItemIds());
    } else {
      setFolderTreeExpandedItems([]);
    }
  };

  const handleExpandedItemsChange = (
    _event: React.SyntheticEvent | null,
    newExpandedItems: string[]
  ) => {
    setFolderTreeExpandedItems(newExpandedItems);
  };

  const handleSelectedItemsChange = (
    _event: React.SyntheticEvent | null,
    itemId: string | null
  ) => {
    if (itemId) {
      navigate(`/documents/${itemId}`);
    }
  };

  useEffect(() => {
    folderService.getFolderTree().then((data) => {
      setFolderTree(data);
    });
  }, [reloadDriveList]);

  return (
    <Box
      role="presentation"
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: showDrawer ? 400 : '100%',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h5">{t('documentTree.folderTree')}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip
            title={
              folderTreeExpandedItems.length === 0
                ? t('documentTree.tooltip.expandAll')
                : t('documentTree.tooltip.collapseAll')
            }
            placement="top"
            arrow
            slotProps={{
              popper: {
                modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
              },
            }}
          >
            <IconButton size="medium" onClick={handleExpandClick}>
              {folderTreeExpandedItems.length === 0 ? (
                <OpenInFullIcon sx={{ width: 20, height: 20 }} />
              ) : (
                <CloseFullscreenIcon sx={{ width: 20, height: 20 }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip
            title={t('documentTree.tooltip.close')}
            placement="top"
            arrow
            slotProps={{
              popper: {
                modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
              },
            }}
          >
            <IconButton size="medium" onClick={() => setShowFolderTree(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Scrollbar sx={{ flex: 1 }}>
        {folderTree.length > 0 && (
          <RichTreeView
            items={folderTree}
            expandedItems={folderTreeExpandedItems}
            selectedItems={folderId || null}
            isItemDisabled={isItemDisabled}
            expansionTrigger="iconContainer"
            onExpandedItemsChange={handleExpandedItemsChange}
            onSelectedItemsChange={handleSelectedItemsChange}
            slots={{
              item: CustomTreeItem,
            }}
            sx={{
              height: 'fit-content',
              flexGrow: 1,
              overflowY: 'auto',
            }}
            itemChildrenIndentation={24}
          />
        )}
      </Scrollbar>
    </Box>
  );
};

export default FolderTree;
